import React, { useEffect, useState } from "react";

export default function AccountAndEnquirySummary({
  accountData,
  enquiryData,
  summary,
}) {
  const data = enquiryData?.enquiries; // your response array

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("+")[0].split("-");

    // Return in DD/MM/YYYY format for the CIBIL UI
    return `${day}/${month}/${year}`;
  };

  const [accountStats, setAccountStats] = useState({
    accounts: {
      total: "",
      zeroBalance: "N/A",
      overdue: "N/A",
    },
    balances: {
      highestBalance: "",
      current: summary?.total_current_balance,
      overdue: summary?.total_amount_overdue,
    },
    openingDate: {
      recent: "",
      oldest: "",
    },
  });


  const formatCurrency = (val) => {
  if (!val || val === "-1" || val === "") return "-";
  const num = Number(val);
  if (isNaN(num)) return val;
  return `₹ ${num.toLocaleString("en-IN")}`;
};

  useEffect(() => {
    if (!accountData) return;

    setAccountStats((prev) => ({
      ...prev,
      accounts: {
        ...prev.accounts,
        total: accountData?.length,
      },
      openingDate: {
        recent: formatDate(accountData?.[0]?.Tradeline?.dateOpened),
        oldest: formatDate(
          accountData?.[accountData?.length - 1]?.Tradeline?.dateOpened,
        ),
      },
    }));
  }, [accountData]);

  const getMonthsAgoDate = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return date;
  };

  const last3Months = getMonthsAgoDate(3);
  const last6Months = getMonthsAgoDate(6);
  const last12Months = getMonthsAgoDate(12);
  const last24Months = getMonthsAgoDate(24);

  let count3Months = 0;
  let count6Months = 0;
  let count12Months = 0;
  let count24Months = 0;

  data?.forEach((item) => {
    // Convert "2026-07-28+05:30" into valid ISO format
    const itemDate = new Date(item?.Inquiry?.inquiryDate?.substring(0, 10));

    if (itemDate >= last3Months) {
      count3Months++;
    }

    if (itemDate >= last6Months) {
      count6Months++;
    }

    if (itemDate >= last12Months) {
      count12Months++;
    }

    if (itemDate >= last24Months) {
      count24Months++;
    }
  });

  // Default values matching your sample image
  const accounts = accountData || {
    total: 15,
    zeroBalance: 12,
    overdue: 2,
    highSanctionedAmount: "₹23,51,519",
    currentBalance: "₹5,82,800",
    overdueBalance: "₹1,74,442",
    recentOpenedDate: "07/03/2019",
    oldestOpenedDate: "26/08/2005",
  };

  const enquiry = enquiryData || {
    totalEnquiries: 14,
  };

  return (
    <div className="w-full max-w-5xl p-6 bg-white custom-font text-gray-900 print:p-0">
      {/* CONSUMER ACCOUNT SUMMARY SECTION */}
      <h2 className="text-base font-bold text-gray-800 mb-2 uppercase tracking-wide">
        CONSUMER ACCOUNT SUMMARY
      </h2>

      <div className="border border-gray-400 rounded-lg grid grid-cols-3 mb-8 overflow-hidden">
        {/* COLUMN 1: ACCOUNTS */}
        <div className="p-4 border-r border-gray-400 flex flex-col">
          <h3 className="text-xs font-bold uppercase text-gray-800 tracking-wider mb-5">
            ACCOUNTS
          </h3>
          <div className="space-y-3 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Total</span>
              <div className="flex items-center gap-6">
                <span className="font-bold">:</span>
                <span className="w-8 text-right font-bold text-black">
                  {accountStats?.accounts.total}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Zero balance</span>
              <div className="flex items-center gap-6">
                <span className="font-bold">:</span>
                <span className="w-8 text-right font-bold text-black">
                  {accountStats?.accounts.zeroBalance}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Overdue</span>
              <div className="flex items-center gap-6">
                <span className="font-bold">:</span>
                <span className="w-8 text-right font-bold text-black">
                  {accountStats?.balances.overdue}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* COLUMN 2: BALANCES */}
        <div className="p-4 border-r border-gray-400 flex flex-col">
          <h3 className="text-xs font-bold uppercase text-gray-800 tracking-wider mb-5">
            BALANCES
          </h3>
          <div className="space-y-3 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">
                High Cr/Sanc. Amt
              </span>
              <div className="flex items-center gap-4">
                <span className="font-bold">:</span>
                <span className="font-bold text-black">{formatCurrency(summary?.total_sanctioned_amount)}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Current</span>
              <div className="flex items-center gap-4">
                <span className="font-bold">:</span>
                <span className="font-bold text-black">
                  {accountStats?.balances.current}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Overdue</span>
              <div className="flex items-center gap-4">
                <span className="font-bold">:</span>
                <span className="font-bold text-black">
                  {accountStats?.balances.overdue}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* COLUMN 3: ACCOUNT OPENED DATE */}
        <div className="p-4 flex flex-col">
          <h3 className="text-xs font-bold uppercase text-gray-800 tracking-wider mb-5">
            ACCOUNT OPENED DATE
          </h3>
          <div className="space-y-3 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Recent</span>
              <div className="flex items-center gap-4">
                <span className="font-bold">:</span>
                <span className="font-bold text-black">
                  {accountStats?.openingDate?.recent}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-800 font-semibold">Oldest</span>
              <div className="flex items-center gap-4">
                <span className="font-bold">:</span>
                <span className="font-bold text-black">
                  {accountStats?.openingDate?.oldest}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ENQUIRY SUMMARY SECTION */}
      <h2 className="text-base font-bold text-gray-800 mb-2 uppercase tracking-wide">
        ENQUIRY SUMMARY
      </h2>

      <div className="border border-gray-400 rounded-lg overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-5 px-4 py-3 text-[11px] font-bold text-[#00a1de] uppercase tracking-wider">
          <div>TOTAL ENQUIRIES</div>
          <div>MOST RECENT</div>
          <div>PAST 6 MONTHS</div>
          <div>PAST 12 MONTHS</div>
          <div>PAST 24 MONTHS</div>
        </div>

        {/* Table Data Row */}
        <div className="grid grid-cols-5 px-4 py-2.5 text-xs font-bold text-black border-t border-gray-300">
          <div>{enquiry.totalEnquiries || "N/A"}</div>
          <div>{count3Months || "N/A"}</div>
          <div>{count6Months || "N/A"}</div>
          <div>{count12Months || "N/A"}</div>
          <div>{count24Months || "N/A"}</div>
        </div>
      </div>
    </div>
  );
}
